import * as THREE from 'three'
import BaseLineTool from '@glass-designer/tools/action/baseLineTool'
import GlassDatumLine from '@glass-designer/model/glassDatumLine'

import Config from '@glass-designer/config'
import MathsUtils from '@/utils/mathsUtils'

import GlassDesignerTypes from '@glass-designer/types'

// Measure tool
// Tool to measure data from point to point
export default class MeasureTool extends BaseLineTool {
  constructor(editor) {
    super(editor)

    this.type = GlassDesignerTypes.ToolTypes.measureTool
    this.showDimension = false

    this.editingLine.setShowOutsideTextLabel(false)
    this.editingLine.setShowInsideTextLabel(false)

    this.traceOptions.ignoreSplitLines = true
  }

  // Tool Active
  // Called when the tool is active
  onToolActive(options = {}) {
    super.onToolActive(options)

    // Start point selections
    this.resetMeasureLine()    

    this.displayToolSettingsPopover()
  }

  // Tool Inactive
  // Called when the tool is no longer active
  onToolInactive() {
    super.onToolInactive()
  }

  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()

    this.editingLine.setVisible(this.lineDetails.startPointSelected && this.lineDetails.endPointSelected)
  }

  // Reset the measure line process
  resetMeasureLine() {
    this.showDimension = false

    this.editor.component.showToolHelpBox('fa-ruler', 'Click and place the start of the measure line')
    this.resetLineDetails()

    this.editor.flagRenderRequired()
  }

  // Display the tool settings popover for this tool
  displayToolSettingsPopover() {
    var lineLength = 0
    var addDatumAvailable = false

    if(this.lineDetails.startPointSelected && this.lineDetails.endPointSelected) {
      addDatumAvailable = true

      lineLength = MathsUtils.roundNumber(this.editingLine.startPoint.distanceTo(this.editingLine.endPoint), 0)
    }

    this.editor.component.displayMeasureToolPopover({
      measureData: {
        lineLength: lineLength,
        addDatumAvailable: addDatumAvailable,
        showDimension: this.showDimension
      },
      context: this,
      cancelCallback: (ctx, userCancelled) => {
        if(userCancelled)
          ctx.editor.cancelActiveTool()
      },
      updateCallback: (ctx, options) => {
        ctx.showDimension = options.showDimension
      },
      actionCallback: (ctx, tag, info) => {
        if(tag == 'addDatum') {
          ctx.addLineDatum(info)
        }
      }
    })
  }

  // onMouseDownEvent
  onMouseDownEvent(ev, mouseData) {
    super.onMouseDownEvent(ev, mouseData)
  }
  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
    super.onMouseMoveEvent(ev, mouseData)
    if(this.lineDetails.settingEndPoint && ev.button == Config.Mouse.buttonSelect) {
      this._updateMeasureLineEndpoint(mouseData)
    }
  }
  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData) {
    super.onMouseUpEvent(ev, mouseData)

    if(ev.button == Config.Mouse.buttonSelect) {
      if(this._updateMeasureLinePoints(mouseData)) {
        this.editor.flagRenderRequired()
        return true
      }
    }

    return false
  }
  onMouseWheelEvent(ev, mouseData) {
    super.onMouseWheelEvent(ev, mouseData)
  }
  onTouchStartEvent(ev, touchData) {
    super.onTouchStartEvent(ev, touchData)
  }
  onTouchEndEvent(ev, touchData) {
    super.onTouchEndEvent(ev, touchData)
    if(this._updateMeasureLinePoints(touchData)) {
      this.editor.flagRenderRequired()
      return true
    }
    return false
  }
  onTouchCancelEvent(ev, touchData) {
    super.onTouchCancelEvent(ev, touchData)
  }
  onTouchMoveEvent(ev, touchData) {
    super.onTouchMoveEvent(ev, touchData)
    if(this.lineDetails.settingEndPoint) {
      this._updateMeasureLineEndpoint(touchData)
    }
  }


  /**
   * Update the measure line endpoint
   * Done during mouse move / touch move
   */
  _updateMeasureLineEndpoint(mouseTouchData) {
    if(!this.selectedShape)
      return
      
    this.lineDetails.endPointSelected = true
    
    var nearestPoint = this.snapToNearestShapePoint(this.selectedShape, new THREE.Vector2(mouseTouchData.position.x, mouseTouchData.position.y)) 
    nearestPoint = this.snapLineToAngle(nearestPoint, true, new THREE.Vector2(mouseTouchData.position.x, mouseTouchData.position.y))

    this.lineDetails.end = nearestPoint

    // Update editing line
    this.editingLine.setStartPoint(this.lineDetails.start.clone())
    this.editingLine.setEndPoint(this.lineDetails.end.clone())    
    this.editingLine.startAngleFixed = this.lineDetails.startAngleFixed
    this.editingLine.endAngleFixed = this.lineDetails.endAngleFixed

    this.editor.flagRenderRequired()
  }


  /**
   * Update the measure line start/end points from mouse/touch event
   * Returns true if the measure line was updated & the mouse/touch event was processed
   */
  _updateMeasureLinePoints(mouseTouchData) {
    if(!this.lineDetails.startPointSelected) {
      if(mouseTouchData.isSnapGridEnabled)
        this.lineDetails.start.set(mouseTouchData.snappedPosition.x, mouseTouchData.snappedPosition.y)
      else
        this.lineDetails.start.set(mouseTouchData.position.x, mouseTouchData.position.y)

      // Get shape closest to the measure line
      this.selectedShape = this.editor.findClosestGlassShape(this.lineDetails.start)
      if(!this.selectedShape)
        return

      this.selectedShapeBounds = this.selectedShape.calculateSegmentBounds()
      this.selectedShapeBounds.getCenter(this.selectedShapeCenter)

      this.lineDetails.startPointSelected = true
      this.lineDetails.settingEndPoint = true

      // Attempt to snap to the nearest vertex on the shape
      let nearestPoint = this.snapToNearestShapePoint(this.selectedShape, new THREE.Vector2(mouseTouchData.position.x, mouseTouchData.position.y))
      this.lineDetails.start = nearestPoint

      // Disable the editing line from touch/mouse events to stop it receiving clicks
      this.editingLine.setEnabled(false)

      this.editor.component.showToolHelpBox('fa-ruler', 'Click and place the end of the measure line')
      return true
    }

    if(this.lineDetails.settingEndPoint) {
      this._updateMeasureLineEndpoint(mouseTouchData)

      this.editor.component.showToolHelpBox('fa-ruler', 'Modify the measure line as required')
      this.lineDetails.settingEndPoint = false

      // Enable the editing line now it's placed
      this.editingLine.setEnabled(true)

      // Set the measure line to match the final traced values
      this.updateMeasureLineToTracedLine()
      this.updateEditingLineLinkedObjects()

      // Setup dimensions for the selected shape & editing line
      this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])

      // Update settings popover
      this.displayToolSettingsPopover()

      return true
    }
  }

  /**
   * Receive an object event from objects we're listening too
   */
  onReceiveObjectEvent(object, eventType, data) {
    if(eventType == GlassDesignerTypes.ObjectEventTypes.objectMoved) {
      if(object == this.editingLine.startHandle) {

        this.lineDetails.start.set(this.editingLine.startHandle.position.x, this.editingLine.startHandle.position.y, this.lineDetails.start.z)

        // Snap measure start point to nearest part of the shape
        let closestPoint = this.snapToNearestShapePoint(this.selectedShape, this.editingLine.startHandle.position)
        closestPoint = this.snapLineToAngle(closestPoint, false, this.editingLine.startHandle.position)

        this.lineDetails.start.set(closestPoint.x, closestPoint.y, this.lineDetails.end.z)

        this.editingLine.setStartPoint(this.lineDetails.start)
        this.updateEditingLineLinkedObjects()

        // Setup dimensions for the selected shape & editing line
        this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])
        
        this.editor.flagRenderRequired()
      }
      else if(object == this.editingLine.endHandle) {

        this.lineDetails.end.set(this.editingLine.endHandle.position.x, this.editingLine.endHandle.position.y, this.lineDetails.end.z)

        // Snap measure end point to nearest part of the shape
        let closestPoint = this.snapToNearestShapePoint(this.selectedShape, this.editingLine.endHandle.position)
        closestPoint = this.snapLineToAngle(closestPoint, true, this.editingLine.endHandle.position)

        this.lineDetails.end.set(closestPoint.x, closestPoint.y, this.lineDetails.end.z)

        this.editingLine.setEndPoint(this.lineDetails.end)
        this.updateEditingLineLinkedObjects()
        
        // Setup dimensions for the selected shape & editing line
        this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])

        this.editor.flagRenderRequired()
      }
      else if(object == this.editingLine) {
        this.lineDetails.start.set(this.lineDetails.start.x + data.moveOffset.x, this.lineDetails.start.y + data.moveOffset.y, this.lineDetails.start.z)
        this.lineDetails.end.set(this.lineDetails.end.x + data.moveOffset.x, this.lineDetails.end.y + data.moveOffset.y, this.lineDetails.end.z)

        // Run trace when moving the editing line
        this.traceEditingLine({
          updateStartPoint: true,
          updateEndPoint: true,
          referencePoint: data.grabPoint
        })

        this.updateEditingLineLinkedObjects()

        // Setup dimensions for the selected shape & editing line
        this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])

        this.editor.flagRenderRequired()
      }

    }

    if(eventType == GlassDesignerTypes.ObjectEventTypes.objectReleased) {

      // Set the measure line to match the final traced values
      this.updateMeasureLineToTracedLine()

      // Update dimensions after moving objects
      this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])

      // Update info on popover
      this.displayToolSettingsPopover()
    }
  }


  /**
   * Update the measure line settings to match the final raytraced points
   */
  updateMeasureLineToTracedLine() {
    this.lineDetails.start = this.editingLine.startPoint.clone()
    this.lineDetails.end = this.editingLine.endPoint.clone()
  }


  /**
   * Add the measurement line as a datum line to the shape
   */
  addLineDatum(options = {}) {
    this.editor.startUndoGroup('Adding Datum Line')
    this.editor.addUndoShape(this.selectedShape)

    var datumLine = new GlassDatumLine(this.editor, {
      startPosition: this.editingLine.startPoint,
      endPosition: this.editingLine.endPoint,
      isDimensionDisplayed: options.showDimension
    })
    
    this.selectedShape.addDatumLine(datumLine)
    this.editor.closeCurrentUndoGroup()

    this.editor.onShapeUpdated(this.selectedShape)

    // Finish
    this.closeTool()
  }

}
